import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Save, Upload, Plus, Edit2, Trash2, RefreshCw, Image, Mail, Play, GripVertical, CheckSquare, Palette, Check, ChevronUp, ChevronDown } from 'lucide-react';
import { applyBrandingPalette } from '../App';

const GRADES = ['A', 'B', 'C', 'D', 'E', 'S', 'F'];
const DIVISIONS = ['Division 1', 'Division 2', 'Division 3', 'Division 4'];

// target_status controls what pipeline stage the applicant is seeded at:
//   received       — eligible, just submitted (tests: review, shortlist flow)
//   screening      — shortlisted (tests: send assessment link)
//   test_invited   — assessment link sent (tests: simulate score → acceptance/rejection)
//   test_completed — assessment done with simulated_score (tests: accept / reject actions)
//   accepted       — admin has accepted (tests: enroll → welcome email)
const DEFAULT_SEED_APPLICANTS = [
  // ── 1: Ineligible — over age (born 1997, age 29) ────────────────────────────
  { full_name: 'James Odhiambo Aloo',    email: 'pratik1@pindush.net',  date_of_birth: '1997-03-01', nationality: 'Kenyan',    llb_status: 'completed',     llb_gpa: 3.9,  llb_gpa_scale: 5, subject1_grade: 'A', subject2_grade: 'A', subject3_grade: 'B', target_status: 'received', simulated_score: 78 },
  // ── 2: Ineligible — GPA too low (2.8 on 5.0 scale) ──────────────────────────
  { full_name: 'Khadija Yusuf Mkwawa',   email: 'pratik2@pindush.net',  date_of_birth: '2001-08-14', nationality: 'Tanzanian', llb_status: 'completed',     llb_gpa: 2.8,  llb_gpa_scale: 5, subject1_grade: 'B', subject2_grade: 'B', subject3_grade: 'C', target_status: 'received', simulated_score: 78 },
  // ── 3: Ineligible — GPA too low (2.5 on 4.0 scale) ──────────────────────────
  { full_name: 'Leonard Chuma Banda',    email: 'pratik3@pindush.net',  date_of_birth: '2002-02-28', nationality: 'Zambian',   llb_status: 'completed',     llb_gpa: 2.5,  llb_gpa_scale: 4, subject1_grade: 'C', subject2_grade: 'D', subject3_grade: 'C', target_status: 'received', simulated_score: 78 },
  // ── 4: Eligible ───────────────────────────────────────────────────────────────
  { full_name: 'Amina Juma Hassan',      email: 'pratik4@pindush.net',  date_of_birth: '2001-03-15', nationality: 'Tanzanian', llb_status: 'completed',     llb_gpa: 3.9,  llb_gpa_scale: 5, subject1_grade: 'A', subject2_grade: 'A', subject3_grade: 'B', target_status: 'received', simulated_score: 78 },
  // ── 5: Eligible ───────────────────────────────────────────────────────────────
  { full_name: 'Brian Omondi Otieno',    email: 'pratik5@pindush.net',  date_of_birth: '2000-07-20', nationality: 'Kenyan',    llb_status: 'completed',     llb_gpa: 4.0,  llb_gpa_scale: 5, subject1_grade: 'A', subject2_grade: 'A', subject3_grade: 'A', target_status: 'received', simulated_score: 78 },
  // ── 6: Eligible ───────────────────────────────────────────────────────────────
  { full_name: 'Fatuma Said Mwamba',     email: 'pratik6@pindush.net',  date_of_birth: '2002-01-10', nationality: 'Tanzanian', llb_status: 'to_be_awarded', llb_gpa: 3.75, llb_gpa_scale: 5, subject1_grade: 'A', subject2_grade: 'B', subject3_grade: 'A', target_status: 'received', simulated_score: 78 },
  // ── 7: Eligible ───────────────────────────────────────────────────────────────
  { full_name: 'Grace Namutebi Kimani',  email: 'pratik7@pindush.net',  date_of_birth: '2000-04-22', nationality: 'Tanzanian', llb_status: 'completed',     llb_gpa: 3.5,  llb_gpa_scale: 5, subject1_grade: 'B', subject2_grade: 'C', subject3_grade: 'B', target_status: 'received', simulated_score: 78 },
  // ── 8: Eligible ───────────────────────────────────────────────────────────────
  { full_name: 'Mary Zawadi Petro',      email: 'pratik8@pindush.net',  date_of_birth: '2001-12-01', nationality: 'Tanzanian', llb_status: 'completed',     llb_gpa: 3.65, llb_gpa_scale: 5, subject1_grade: 'A', subject2_grade: 'B', subject3_grade: 'B', target_status: 'received', simulated_score: 78 },
  // ── 9: Eligible ───────────────────────────────────────────────────────────────
  { full_name: 'Nickson Twalib Rashid',  email: 'pratik9@pindush.net',  date_of_birth: '2000-05-17', nationality: 'Tanzanian', llb_status: 'to_be_awarded', llb_gpa: 3.55, llb_gpa_scale: 5, subject1_grade: 'B', subject2_grade: 'A', subject3_grade: 'B', target_status: 'received', simulated_score: 78 },
  // ── 10: Eligible ──────────────────────────────────────────────────────────────
  { full_name: 'Isabella Mwangi Ngugi',  email: 'pratik10@pindush.net', date_of_birth: '2001-06-30', nationality: 'Tanzanian', llb_status: 'completed',     llb_gpa: 3.7,  llb_gpa_scale: 5, subject1_grade: 'A', subject2_grade: 'B', subject3_grade: 'A', target_status: 'received', simulated_score: 78 },
];

function PaletteCard({ palette, active, saved, onPreview, onEdit, onDelete }) {
  const c = palette.colors || {};
  return (
    <div
      onClick={onPreview}
      className={`rounded-lg border-2 p-4 cursor-pointer transition-all ${active ? 'border-blue-500 shadow-md' : 'border-gray-200 hover:border-gray-400'}`}
    >
      {/* Colour swatches */}
      <div className="flex gap-1.5 mb-3">
        <div className="w-8 h-8 rounded-full border border-white/20 shadow-sm" style={{ backgroundColor: c.primary }} title="Primary" />
        <div className="w-8 h-8 rounded-full border border-white/20 shadow-sm" style={{ backgroundColor: c.secondary }} title="Secondary / CTA" />
        <div className="w-8 h-8 rounded-full border border-white/20 shadow-sm" style={{ backgroundColor: c.accent }} title="Accent" />
        <div className="w-8 h-8 rounded-full border border-gray-200 shadow-sm" style={{ backgroundColor: c.background }} title="Background" />
      </div>
      {/* Mini preview bar */}
      <div className="rounded h-6 mb-3 flex overflow-hidden" style={{ backgroundColor: c.primary }}>
        <div className="w-2/3 h-full flex items-center px-2">
          <span className="text-[10px] font-semibold truncate" style={{ color: c.primary_fg }}>{palette.name}</span>
        </div>
        <div className="w-1/3 h-full" style={{ backgroundColor: c.secondary }} />
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-800">{palette.name}</p>
          {saved && <span className="text-[10px] text-green-700 bg-green-100 px-1.5 py-0.5 rounded font-medium">Active</span>}
          {active && !saved && <span className="text-[10px] text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded font-medium">Previewing</span>}
        </div>
        {!palette.preset && (
          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
            <button onClick={onEdit} className="p-1 text-gray-400 hover:text-gray-700"><Edit2 className="w-3.5 h-3.5" /></button>
            <button onClick={onDelete} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>
    </div>
  );
}

const SiteSettings = () => {
  const { tab: activeSettingsTab = 'content' } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Site Configuration
  const [siteConfig, setSiteConfig] = useState({});
  
  // FAQs
  const [faqs, setFaqs] = useState([]);
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', order: 0 });
  
  // Admission Settings
  const [admissionSettings, setAdmissionSettings] = useState({
    admissions_open: true,
    closed_message: ''
  });
  
  // Email Templates
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  // Scoring Config
  const [scoringConfig, setScoringConfig] = useState({ gpa_weight: 60, a_level_weight: 40, gpa_min_4: 2.8, gpa_min_5: 3.3 });
  const [finalScoringConfig, setFinalScoringConfig] = useState({ final_gpa_weight: 40, final_a_level_weight: 30, final_aptitude_weight: 30 });
  const [scoringError, setScoringError] = useState('');
  const [finalScoringError, setFinalScoringError] = useState('');
  const [recalculating, setRecalculating] = useState(false);

  // Email preview
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [sendingPreview, setSendingPreview] = useState(false);

  // System Config
  const [systemConfig, setSystemConfig] = useState({ smtp2go_configured: false, smtp2go_key_preview: '', sender_email: '', sender_name: '', frontend_url: '' });
  const [systemConfigForm, setSystemConfigForm] = useState({ smtp2go_api_key: '', sender_email: '', sender_name: '', frontend_url: '' });
  const [savingSystem, setSavingSystem] = useState(false);

  // Passage Bank
  const [passages, setPassages] = useState([]);
  const [loadingPassages, setLoadingPassages] = useState(false);
  const [expandedPassage, setExpandedPassage] = useState(null); // passage id currently expanded
  const [passageQuestions, setPassageQuestions] = useState({}); // {passage_id: [questions]}
  const [testConfig, setTestConfig] = useState({ passages_per_test: 6, time_limit_minutes: 60, link_expiry_hours: 8 });
  const [savingTestConfig, setSavingTestConfig] = useState(false);
  const [passageDialog, setPassageDialog] = useState(false);
  const [passageForm, setPassageForm] = useState({ id: null, title: '', text: '', source: '' });
  const [savingPassage, setSavingPassage] = useState(false);
  const [questionDialog, setQuestionDialog] = useState(false);
  const [questionForm, setQuestionForm] = useState({ id: null, passage_id: '', text: '', options: ['', '', '', '', ''], correct_answer: 'A', question_type: '' });
  const [savingQuestion, setSavingQuestion] = useState(false);

  // Review Checklist Config
  const [checklistSections, setChecklistSections] = useState([]);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistSaving, setChecklistSaving] = useState(false);

  // Dev Tools
  const [seedApplicants, setSeedApplicants] = useState(DEFAULT_SEED_APPLICANTS.map((a, i) => ({ ...a, _id: i })));
  const [seedForce, setSeedForce] = useState(false);
  const [seedRunning, setSeedRunning] = useState(false);
  const [seedResults, setSeedResults] = useState(null);
  const [deleteRunning, setDeleteRunning] = useState(false);
  const [editingApplicant, setEditingApplicant] = useState(null);
  const [seedingRow, setSeedingRow] = useState(null);
  const [migrateRunning, setMigrateRunning] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);
  const [devIntakeId, setDevIntakeId] = useState('');
  const [intakesList, setIntakesList] = useState([]);
  const [testInvitedApps, setTestInvitedApps] = useState([]);
  const [simScores, setSimScores] = useState({});
  const [simulating, setSimulating] = useState({});
  const [loadingTestInvited, setLoadingTestInvited] = useState(false);

  // Branding
  const [brandingConfig, setBrandingConfig] = useState({ active_palette_id: 'navy-red', palettes: [] });
  const [previewPaletteId, setPreviewPaletteId] = useState(null); // live-preview without saving
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [customPaletteForm, setCustomPaletteForm] = useState(null); // null = closed
  const [editingCustomId, setEditingCustomId] = useState(null);

  useEffect(() => {
    fetchAllSettings();
  }, []);

  useEffect(() => {
    if (activeSettingsTab === 'questions') {
      fetchPassages();
    }
    if (activeSettingsTab === 'checklist') {
      fetchChecklistConfig();
    }
    if (activeSettingsTab === 'branding') {
      fetchBrandingConfig();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSettingsTab]);

  const fetchAllSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [configRes, faqsRes, admissionRes, templatesRes, scoringRes, systemRes, intakesRes] = await Promise.all([
        axios.get(`${API}/site-config`, config).catch(() => ({ data: {} })),
        axios.get(`${API}/faqs/all`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/admission-settings`, config).catch(() => ({ data: { admissions_open: true } })),
        axios.get(`${API}/email-templates`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/scoring-config`, config).catch(() => ({ data: { gpa_weight: 60, a_level_weight: 40 } })),
        axios.get(`${API}/system-config`, config).catch(() => ({ data: {} })),
        axios.get(`${API}/intake-periods`, config).catch(() => ({ data: [] })),
      ]);
      const intakes = Array.isArray(intakesRes.data) ? intakesRes.data : (intakesRes.data?.intakes || []);
      setIntakesList(intakes);
      // Auto-select: prefer first open intake, else first intake overall
      if (intakes.length > 0) {
        const openIntake = intakes.find(i => i.status === 'open' || i.status === 'reviewing');
        setDevIntakeId(prev => prev || (openIntake || intakes[0]).id);
      }

      setSiteConfig(configRes.data);
      setFaqs(faqsRes.data);
      const scoring = scoringRes.data;
      setScoringConfig({ gpa_weight: scoring.gpa_weight ?? 60, a_level_weight: scoring.a_level_weight ?? 40, gpa_min_4: scoring.gpa_min_4 ?? 2.8, gpa_min_5: scoring.gpa_min_5 ?? 3.3 });
      setFinalScoringConfig({
        final_gpa_weight:      scoring.final_gpa_weight      ?? 40,
        final_a_level_weight:  scoring.final_a_level_weight  ?? 30,
        final_aptitude_weight: scoring.final_aptitude_weight ?? 30,
      });
      setAdmissionSettings(admissionRes.data);
      setEmailTemplates(templatesRes.data);
      const sc = systemRes.data;
      setSystemConfig(sc);
      setSystemConfigForm({ smtp2go_api_key: '', sender_email: sc.sender_email || '', sender_name: sc.sender_name || '', frontend_url: sc.frontend_url || '' });
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // ── Dev Tools Handlers ─────────────────────────────────────────────────────

  const fetchTestInvited = async () => {
    if (!devIntakeId) { toast.error('Select an intake first'); return; }
    setLoadingTestInvited(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/applications`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { intake_id: devIntakeId },
      });
      const all = Array.isArray(res.data) ? res.data : (res.data?.applications || []);
      const invited = all.filter(a => a.status === 'test_invited' || a.status === 'test_sent');
      setTestInvitedApps(invited);
      const init = {};
      invited.forEach(a => { init[a.id] = 78; });
      setSimScores(prev => ({ ...init, ...prev }));
      if (invited.length === 0) toast.info('No applicants in test_invited status for this intake');
    } catch { toast.error('Failed to load assessment candidates'); }
    finally { setLoadingTestInvited(false); }
  };

  const handleSimulateAssessment = async (appId) => {
    const score = parseFloat(simScores[appId] ?? 78);
    if (isNaN(score) || score < 0 || score > 100) { toast.error('Score must be 0–100'); return; }
    setSimulating(prev => ({ ...prev, [appId]: true }));
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/dev/simulate-assessment/${appId}?score=${score}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${res.data.applicant_name} — score ${res.data.score}%, final ${res.data.final_score}% ✓`);
      setTestInvitedApps(prev => prev.filter(a => a.id !== appId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Simulation failed');
    } finally {
      setSimulating(prev => ({ ...prev, [appId]: false }));
    }
  };

  // ── Branding Handlers ──────────────────────────────────────────────────────

  const fetchBrandingConfig = async () => {
    try {
      const res = await axios.get(`${API}/branding-config`);
      setBrandingConfig(res.data);
      setPreviewPaletteId(res.data.active_palette_id);
    } catch { /* ignore */ }
  };

  const previewPalette = (paletteId) => {
    const palette = brandingConfig.palettes.find(p => p.id === paletteId);
    if (palette?.colors) {
      applyBrandingPalette(palette.colors);
      setPreviewPaletteId(paletteId);
    }
  };

  const saveBranding = async () => {
    setBrandingSaving(true);
    try {
      const token = localStorage.getItem('token');
      const custom = brandingConfig.palettes.filter(p => !p.preset);
      const res = await axios.put(
        `${API}/branding-config`,
        { active_palette_id: previewPaletteId, palettes: custom },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBrandingConfig(res.data);
      toast.success('Branding saved');
    } catch {
      toast.error('Failed to save branding');
    } finally {
      setBrandingSaving(false);
    }
  };

  const EMPTY_CUSTOM_PALETTE = () => ({
    id: `custom-${Date.now()}`,
    name: '',
    preset: false,
    colors: { primary: '#0F172A', primary_fg: '#F8FAFC', secondary: '#B11F24', secondary_fg: '#FFFFFF', accent: '#334155', accent_fg: '#F8FAFC', muted: '#F1F5F9', muted_fg: '#64748B', background: '#FDFBF7' },
  });

  const openNewCustomPalette = () => {
    setEditingCustomId(null);
    setCustomPaletteForm(EMPTY_CUSTOM_PALETTE());
  };

  const openEditCustomPalette = (palette) => {
    setEditingCustomId(palette.id);
    setCustomPaletteForm({ ...palette, colors: { ...palette.colors } });
  };

  const saveCustomPalette = () => {
    if (!customPaletteForm.name.trim()) { toast.error('Palette name required'); return; }
    setBrandingConfig(prev => {
      const custom = prev.palettes.filter(p => !p.preset && p.id !== customPaletteForm.id);
      return { ...prev, palettes: [...prev.palettes.filter(p => p.preset), ...custom, customPaletteForm] };
    });
    setCustomPaletteForm(null);
    setEditingCustomId(null);
  };

  const deleteCustomPalette = (id) => {
    setBrandingConfig(prev => ({ ...prev, palettes: prev.palettes.filter(p => p.id !== id) }));
    if (previewPaletteId === id) setPreviewPaletteId('navy-red');
  };

  // ── Review Checklist Handlers ──────────────────────────────────────────────

  const genKey = (label) => {
    const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'item';
    return `${slug}_${Math.random().toString(36).substr(2, 6)}`;
  };

  const fetchChecklistConfig = async () => {
    setChecklistLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/review-checklist-config`, { headers: { Authorization: `Bearer ${token}` } });
      setChecklistSections(res.data.sections || []);
    } catch {
      toast.error('Failed to load checklist config');
    } finally {
      setChecklistLoading(false);
    }
  };

  const saveChecklistConfig = async () => {
    setChecklistSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/review-checklist-config`, { sections: checklistSections }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Checklist config saved');
    } catch {
      toast.error('Failed to save checklist config');
    } finally {
      setChecklistSaving(false);
    }
  };

  const clAddSection = () => {
    const label = 'New Section';
    setChecklistSections(prev => [...prev, {
      id: `section_${Math.random().toString(36).substr(2, 6)}`,
      label,
      doc_idx: null,
      items: [],
    }]);
  };

  const clRemoveSection = (sIdx) => {
    if (!window.confirm('Remove this section and all its items?')) return;
    setChecklistSections(prev => prev.filter((_, i) => i !== sIdx));
  };

  const clUpdateSectionLabel = (sIdx, label) => {
    setChecklistSections(prev => prev.map((s, i) => i === sIdx ? { ...s, label } : s));
  };

  const clUpdateDocIdx = (sIdx, val) => {
    const parsed = val === '' ? null : parseInt(val, 10);
    setChecklistSections(prev => prev.map((s, i) => i === sIdx ? { ...s, doc_idx: isNaN(parsed) ? null : parsed } : s));
  };

  const clAddItem = (sIdx) => {
    const label = 'New checklist item';
    setChecklistSections(prev => prev.map((s, i) => i === sIdx
      ? { ...s, items: [...s.items, { key: genKey(label), label }] }
      : s
    ));
  };

  const clRemoveItem = (sIdx, iIdx) => {
    setChecklistSections(prev => prev.map((s, i) => i === sIdx
      ? { ...s, items: s.items.filter((_, j) => j !== iIdx) }
      : s
    ));
  };

  const clUpdateItemLabel = (sIdx, iIdx, label) => {
    setChecklistSections(prev => prev.map((s, i) => i === sIdx
      ? { ...s, items: s.items.map((item, j) => j === iIdx ? { ...item, label } : item) }
      : s
    ));
  };

  const clMoveSectionUp = (sIdx) => {
    if (sIdx === 0) return;
    setChecklistSections(prev => {
      const next = [...prev];
      [next[sIdx - 1], next[sIdx]] = [next[sIdx], next[sIdx - 1]];
      return next;
    });
  };

  const clMoveSectionDown = (sIdx) => {
    setChecklistSections(prev => {
      if (sIdx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[sIdx], next[sIdx + 1]] = [next[sIdx + 1], next[sIdx]];
      return next;
    });
  };

  const clMoveItemUp = (sIdx, iIdx) => {
    if (iIdx === 0) return;
    setChecklistSections(prev => prev.map((s, i) => {
      if (i !== sIdx) return s;
      const items = [...s.items];
      [items[iIdx - 1], items[iIdx]] = [items[iIdx], items[iIdx - 1]];
      return { ...s, items };
    }));
  };

  const clMoveItemDown = (sIdx, iIdx) => {
    setChecklistSections(prev => prev.map((s, i) => {
      if (i !== sIdx) return s;
      if (iIdx >= s.items.length - 1) return s;
      const items = [...s.items];
      [items[iIdx], items[iIdx + 1]] = [items[iIdx + 1], items[iIdx]];
      return { ...s, items };
    }));
  };

  // Passage Bank Handlers
  const authHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchPassages = async () => {
    setLoadingPassages(true);
    try {
      const res = await axios.get(`${API}/aptitude-passages`, { headers: authHeader() });
      setPassages(res.data);
      const cfg = await axios.get(`${API}/aptitude-test-config`, { headers: authHeader() });
      setTestConfig(cfg.data);
    } catch { toast.error('Failed to load passages'); }
    finally { setLoadingPassages(false); }
  };

  const fetchPassageQuestions = async (passageId) => {
    try {
      const res = await axios.get(`${API}/aptitude-passages/${passageId}/questions`, { headers: authHeader() });
      setPassageQuestions(prev => ({ ...prev, [passageId]: res.data }));
    } catch { toast.error('Failed to load questions for passage'); }
  };

  const handleTogglePassage = async (passageId) => {
    if (expandedPassage === passageId) {
      setExpandedPassage(null);
    } else {
      setExpandedPassage(passageId);
      if (!passageQuestions[passageId]) {
        await fetchPassageQuestions(passageId);
      }
    }
  };

  const handleSaveTestConfig = async () => {
    setSavingTestConfig(true);
    try {
      await axios.put(`${API}/aptitude-test-config`, testConfig, { headers: authHeader() });
      toast.success('Test configuration saved');
    } catch { toast.error('Failed to save config'); }
    finally { setSavingTestConfig(false); }
  };

  const handleSavePassage = async () => {
    if (!passageForm.title.trim() || !passageForm.text.trim()) {
      toast.error('Title and passage text are required');
      return;
    }
    setSavingPassage(true);
    try {
      if (passageForm.id) {
        await axios.put(`${API}/aptitude-passages/${passageForm.id}`, passageForm, { headers: authHeader() });
        toast.success('Passage updated');
      } else {
        await axios.post(`${API}/aptitude-passages`, passageForm, { headers: authHeader() });
        toast.success('Passage added');
      }
      setPassageDialog(false);
      setPassageForm({ id: null, title: '', text: '', source: '' });
      fetchPassages();
    } catch { toast.error('Failed to save passage'); }
    finally { setSavingPassage(false); }
  };

  const handleDeletePassage = async (id) => {
    if (!window.confirm('Delete this passage and all its questions?')) return;
    try {
      await axios.delete(`${API}/aptitude-passages/${id}`, { headers: authHeader() });
      toast.success('Passage deleted');
      fetchPassages();
    } catch { toast.error('Failed to delete passage'); }
  };

  const handleTogglePassageActive = async (id, currentActive) => {
    try {
      await axios.put(`${API}/aptitude-passages/${id}`, { active: !currentActive }, { headers: authHeader() });
      setPassages(prev => prev.map(p => p.id === id ? { ...p, active: !currentActive } : p));
    } catch { toast.error('Failed to update passage'); }
  };

  const handleSaveQuestion = async () => {
    if (!questionForm.text.trim() || questionForm.options.some(o => !o.trim())) {
      toast.error('Question text and all options are required');
      return;
    }
    setSavingQuestion(true);
    try {
      const payload = { ...questionForm, options: questionForm.options.filter(o => o.trim()) };
      if (questionForm.id) {
        await axios.put(`${API}/aptitude-questions/${questionForm.id}`, payload, { headers: authHeader() });
        toast.success('Question updated');
      } else {
        await axios.post(`${API}/aptitude-questions`, payload, { headers: authHeader() });
        toast.success('Question added');
      }
      setQuestionDialog(false);
      setQuestionForm({ id: null, passage_id: '', text: '', options: ['', '', '', '', ''], correct_answer: 'A', question_type: '' });
      if (questionForm.passage_id) fetchPassageQuestions(questionForm.passage_id);
    } catch { toast.error('Failed to save question'); }
    finally { setSavingQuestion(false); }
  };

  const handleDeleteQuestion = async (id, passageId) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await axios.delete(`${API}/aptitude-questions/${id}`, { headers: authHeader() });
      toast.success('Question deleted');
      fetchPassageQuestions(passageId);
    } catch { toast.error('Failed to delete question'); }
  };

  const moveQuestion = async (passageId, qi, direction) => {
    const qs = [...passageQuestions[passageId]];
    const swapIdx = qi + direction;
    if (swapIdx < 0 || swapIdx >= qs.length) return;
    const updated = qs.map((q, i) => ({ ...q, sort_order: i }));
    [updated[qi].sort_order, updated[swapIdx].sort_order] = [updated[swapIdx].sort_order, updated[qi].sort_order];
    [updated[qi], updated[swapIdx]] = [updated[swapIdx], updated[qi]];
    setPassageQuestions(prev => ({ ...prev, [passageId]: updated }));
    try {
      await Promise.all([
        axios.put(`${API}/aptitude-questions/${updated[qi].id}`, { sort_order: updated[qi].sort_order }, { headers: authHeader() }),
        axios.put(`${API}/aptitude-questions/${updated[swapIdx].id}`, { sort_order: updated[swapIdx].sort_order }, { headers: authHeader() }),
      ]);
    } catch { toast.error('Failed to reorder'); fetchPassageQuestions(passageId); }
  };

  // Site Configuration Handlers
  const handleConfigChange = (field, value) => {
    setSiteConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (field, file) => {
    if (!file) return;
    
    const maxSize = 2097152; // 2MB
    if (file.size > maxSize) {
      toast.error('Image size must be less than 2MB');
      return;
    }
    
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, and WebP images are allowed');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setSiteConfig(prev => ({ ...prev, [field]: reader.result }));
      toast.success('Image uploaded');
    };
    reader.readAsDataURL(file);
  };

  const saveSiteConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      // Only include *_data fields if the user actually picked a new image this session
      // (i.e. they're base64 data: URIs). Exclude stale/empty values to keep payload small.
      const IMAGE_DATA_FIELDS = ['logo_data', 'email_logo_data', 'hero_image_data', 'hero_image_mobile_data', 'about_image_data'];
      const payload = { ...siteConfig };
      IMAGE_DATA_FIELDS.forEach(f => {
        if (!payload[f] || !String(payload[f]).startsWith('data:')) delete payload[f];
      });
      await axios.put(`${API}/site-config`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Clear base64 blobs from state after successful upload — URLs are now in *_url fields
      setSiteConfig(prev => {
        const next = { ...prev };
        IMAGE_DATA_FIELDS.forEach(f => { next[f] = null; });
        return next;
      });
      toast.success('Site configuration saved successfully');
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // FAQ Handlers
  const openFaqDialog = (faq = null) => {
    if (faq) {
      setEditingFaq(faq);
      setFaqForm({ question: faq.question, answer: faq.answer, order: faq.order || 0 });
    } else {
      setEditingFaq(null);
      setFaqForm({ question: '', answer: '', order: faqs.length + 1 });
    }
    setFaqDialogOpen(true);
  };

  const saveFaq = async () => {
    if (!faqForm.question || !faqForm.answer) {
      toast.error('Please fill in all fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingFaq) {
        await axios.put(`${API}/faqs/${editingFaq.id}`, faqForm, config);
        toast.success('FAQ updated');
      } else {
        await axios.post(`${API}/faqs`, faqForm, config);
        toast.success('FAQ created');
      }

      setFaqDialogOpen(false);
      fetchAllSettings();
    } catch (error) {
      toast.error('Failed to save FAQ');
    } finally {
      setSaving(false);
    }
  };

  const deleteFaq = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/faqs/${faqId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('FAQ deleted');
      fetchAllSettings();
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  // Admission Settings Handlers
  const saveAdmissionSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admission-settings`, admissionSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Admission settings saved');
    } catch (error) {
      toast.error('Failed to save admission settings');
    } finally {
      setSaving(false);
    }
  };

  // Email Template Handlers
  const openTemplateDialog = (template) => {
    setEditingTemplate({ ...template });
    setTemplateDialogOpen(true);
  };

  const saveTemplate = async () => {
    if (!editingTemplate) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/email-templates/${editingTemplate.id}`, {
        subject: editingTemplate.subject,
        body_html: editingTemplate.body_html
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email template saved');
      setTemplateDialogOpen(false);
      fetchAllSettings();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this email template?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/email-templates/${templateId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Template deleted');
      fetchAllSettings();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const initializeTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/email-templates/initialize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Default templates initialized');
      fetchAllSettings();
    } catch (error) {
      toast.error('Failed to initialize templates');
    }
  };

  const initializeFaqs = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/faqs/initialize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Default FAQs initialized');
      fetchAllSettings();
    } catch (error) {
      toast.error('Failed to initialize FAQs');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-xl">Loading settings...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Page header */}
      <div className="bg-white border-b px-6 py-4">
        <h1 className="text-xl font-playfair font-bold text-primary">Site Settings</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Manage landing page, emails, admissions and system configuration</p>
      </div>
      <div className="p-6 space-y-6">

        {/* Landing Page Content */}
        {activeSettingsTab === 'content' && (
          <Card>
            <CardHeader>
              <CardTitle>Landing Page Content</CardTitle>
              <CardDescription>Customize all text and images on the public landing page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Logo */}
              <div className="space-y-4 p-4 bg-muted rounded-lg">
                <h3 className="font-bold text-lg flex items-center">
                  <Image className="w-5 h-5 mr-2" />
                  Logo
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-muted-foreground">Site Logo (shown on website)</p>
                    {(siteConfig.logo_data || siteConfig.logo_url) && (
                      <img src={siteConfig.logo_data || siteConfig.logo_url} alt="Logo" className="h-12 bg-gray-100 p-2 rounded" />
                    )}
                    <Input type="file" accept="image/*" onChange={(e) => handleImageUpload('logo_data', e.target.files[0])} />
                  </div>
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-muted-foreground">Email Logo (shown in email headers)</p>
                    {(siteConfig.email_logo_data || siteConfig.email_logo_url) && (
                      <img src={siteConfig.email_logo_data || siteConfig.email_logo_url} alt="Email Logo" className="h-12 bg-gray-800 p-2 rounded" />
                    )}
                    <Input type="file" accept="image/*" onChange={(e) => handleImageUpload('email_logo_data', e.target.files[0])} />
                    <p className="text-xs text-muted-foreground">Use a light/white logo — email headers have a dark background. Falls back to site logo if not set.</p>
                  </div>
                </div>
              </div>

              {/* Hero Section */}
              <div className="space-y-4 p-4 bg-muted rounded-lg">
                <h3 className="font-bold text-lg">Hero Section</h3>
                <div className="grid gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input 
                      value={siteConfig.hero_title || ''}
                      onChange={(e) => handleConfigChange('hero_title', e.target.value)}
                      placeholder="Build Your Legal Career"
                    />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Textarea 
                      value={siteConfig.hero_subtitle || ''}
                      onChange={(e) => handleConfigChange('hero_subtitle', e.target.value)}
                      placeholder="Practical legal skills academy..."
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Button Text</Label>
                    <Input 
                      value={siteConfig.hero_button_text || ''}
                      onChange={(e) => handleConfigChange('hero_button_text', e.target.value)}
                      placeholder="Start Your Journey"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Hero Image — Desktop (landscape)</Label>
                      {(siteConfig.hero_image_data || siteConfig.hero_image_url) && (
                        <img
                          src={siteConfig.hero_image_data || siteConfig.hero_image_url}
                          alt="Hero Desktop"
                          className="h-24 w-full object-cover mt-2 rounded"
                        />
                      )}
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload('hero_image_data', e.target.files[0])}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Hero Image — Mobile (portrait)</Label>
                      <p className="text-xs text-muted-foreground mb-1">If not set, desktop image is used on mobile.</p>
                      {(siteConfig.hero_image_mobile_data || siteConfig.hero_image_mobile_url) && (
                        <img
                          src={siteConfig.hero_image_mobile_data || siteConfig.hero_image_mobile_url}
                          alt="Hero Mobile"
                          className="h-24 w-full object-cover mt-2 rounded"
                        />
                      )}
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload('hero_image_mobile_data', e.target.files[0])}
                        className="mt-2"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* About Section */}
              <div className="space-y-4 p-4 bg-muted rounded-lg">
                <h3 className="font-bold text-lg">About Section</h3>
                <div className="grid gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input 
                      value={siteConfig.about_title || ''}
                      onChange={(e) => handleConfigChange('about_title', e.target.value)}
                      placeholder="About the Academy"
                    />
                  </div>
                  <div>
                    <Label>Paragraph 1</Label>
                    <Textarea 
                      value={siteConfig.about_paragraph_1 || ''}
                      onChange={(e) => handleConfigChange('about_paragraph_1', e.target.value)}
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Paragraph 2</Label>
                    <Textarea 
                      value={siteConfig.about_paragraph_2 || ''}
                      onChange={(e) => handleConfigChange('about_paragraph_2', e.target.value)}
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Paragraph 3</Label>
                    <Textarea 
                      value={siteConfig.about_paragraph_3 || ''}
                      onChange={(e) => handleConfigChange('about_paragraph_3', e.target.value)}
                      rows={3}
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>About Image</Label>
                      {(siteConfig.about_image_data || siteConfig.about_image_url) && (
                        <img 
                          src={siteConfig.about_image_data || siteConfig.about_image_url} 
                          alt="About" 
                          className="h-24 w-full object-cover mt-2 rounded"
                        />
                      )}
                    </div>
                    <div>
                      <Label>Upload New About Image</Label>
                      <Input 
                        type="file" 
                        accept="image/*"
                        onChange={(e) => handleImageUpload('about_image_data', e.target.files[0])}
                        className="mt-2"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Program Section */}
              <div className="space-y-4 p-4 bg-muted rounded-lg">
                <h3 className="font-bold text-lg">Program Structure Section</h3>
                <div className="grid gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input 
                      value={siteConfig.program_title || ''}
                      onChange={(e) => handleConfigChange('program_title', e.target.value)}
                      placeholder="Program Structure"
                    />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Input 
                      value={siteConfig.program_subtitle || ''}
                      onChange={(e) => handleConfigChange('program_subtitle', e.target.value)}
                      placeholder="A comprehensive curriculum designed for aspiring legal professionals"
                    />
                  </div>
                </div>

                {/* Program Modules */}
                <div className="mt-6">
                  <Label className="text-base font-semibold">Program Modules</Label>
                  <p className="text-sm text-gray-500 mb-4">Manage the program modules displayed on the landing page</p>
                  
                  <div className="space-y-4">
                    {(siteConfig.program_modules || []).map((module, index) => (
                      <div key={index} className="bg-white p-4 rounded-sm border space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-primary">Module {index + 1}</span>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              const newModules = (siteConfig.program_modules || []).filter((_, i) => i !== index);
                              handleConfigChange('program_modules', newModules);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="grid md:grid-cols-4 gap-3">
                          <div>
                            <Label className="text-sm">Number</Label>
                            <Input
                              value={module.number || ''}
                              onChange={(e) => {
                                const newModules = [...(siteConfig.program_modules || [])];
                                newModules[index] = { ...newModules[index], number: e.target.value };
                                handleConfigChange('program_modules', newModules);
                              }}
                              placeholder="01"
                            />
                          </div>
                          <div className="md:col-span-3">
                            <Label className="text-sm">Title</Label>
                            <Input
                              value={module.title || ''}
                              onChange={(e) => {
                                const newModules = [...(siteConfig.program_modules || [])];
                                newModules[index] = { ...newModules[index], title: e.target.value };
                                handleConfigChange('program_modules', newModules);
                              }}
                              placeholder="Module title"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-sm">Items (one per line)</Label>
                          <Textarea
                            value={(module.items || []).join('\n')}
                            onChange={(e) => {
                              const newModules = [...(siteConfig.program_modules || [])];
                              newModules[index] = { ...newModules[index], items: e.target.value.split('\n').filter(s => s.trim()) };
                              handleConfigChange('program_modules', newModules);
                            }}
                            rows={4}
                            placeholder="Legal research & writing&#10;Case analysis&#10;Legal drafting&#10;Ethics & conduct"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button
                    variant="outline"
                    onClick={() => {
                      const newModules = [...(siteConfig.program_modules || []), { number: String((siteConfig.program_modules || []).length + 1).padStart(2, '0'), title: '', items: [] }];
                      handleConfigChange('program_modules', newModules);
                    }}
                    className="w-full mt-4"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Program Module
                  </Button>
                </div>
              </div>

              {/* CTA Section */}
              <div className="space-y-4 p-4 bg-muted rounded-lg">
                <h3 className="font-bold text-lg">Call to Action Section</h3>
                <div className="grid gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input 
                      value={siteConfig.cta_title || ''}
                      onChange={(e) => handleConfigChange('cta_title', e.target.value)}
                      placeholder="Ready to Start Your Journey?"
                    />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Input 
                      value={siteConfig.cta_subtitle || ''}
                      onChange={(e) => handleConfigChange('cta_subtitle', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Button Text</Label>
                    <Input 
                      value={siteConfig.cta_button_text || ''}
                      onChange={(e) => handleConfigChange('cta_button_text', e.target.value)}
                      placeholder="Apply Now"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-4 p-4 bg-muted rounded-lg">
                <h3 className="font-bold text-lg">Contact Information</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>Email</Label>
                    <Input 
                      value={siteConfig.contact_email || ''}
                      onChange={(e) => handleConfigChange('contact_email', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input 
                      value={siteConfig.contact_phone || ''}
                      onChange={(e) => handleConfigChange('contact_phone', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Textarea
                      value={siteConfig.contact_address || ''}
                      onChange={(e) => handleConfigChange('contact_address', e.target.value)}
                      rows={3}
                      placeholder={"FB Attorneys Academy,\n8th Floor, Amani Place,\nOhio Street, Dar es Salaam."}
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="space-y-4 p-4 bg-muted rounded-lg">
                <h3 className="font-bold text-lg">Footer</h3>
                <div>
                  <Label>Footer Description</Label>
                  <Textarea 
                    value={siteConfig.footer_description || ''}
                    onChange={(e) => handleConfigChange('footer_description', e.target.value)}
                    rows={2}
                  />
                </div>
              </div>

              <Button onClick={saveSiteConfig} disabled={saving} className="w-full" data-testid="save-site-config-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save All Changes'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* FAQs Management */}
        {activeSettingsTab === 'faqs' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>FAQ Management</CardTitle>
                <CardDescription>Add, edit, or remove frequently asked questions</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={initializeFaqs} data-testid="init-faqs-btn">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Initialize Defaults
                </Button>
                <Button onClick={() => openFaqDialog()} data-testid="add-faq-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add FAQ
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order</TableHead>
                    <TableHead>Question</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {faqs.map((faq) => (
                    <TableRow key={faq.id} data-testid={`faq-row-${faq.id}`}>
                      <TableCell>{faq.order}</TableCell>
                      <TableCell className="max-w-md truncate">{faq.question}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs ${faq.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {faq.active ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline" onClick={() => openFaqDialog(faq)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => deleteFaq(faq.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {faqs.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No FAQs yet. Click "Initialize Defaults" to add default FAQs or "Add FAQ" to create new ones.
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Eligibility Criteria */}
        {activeSettingsTab === 'eligibility' && (
          <Card>
            <CardHeader>
              <CardTitle>Eligibility Criteria</CardTitle>
              <CardDescription>Manage the eligibility criteria shown on the application form</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-gray-600">
                These criteria are displayed on the application form to inform applicants of the requirements.
              </p>
              
              {(siteConfig.eligibility_criteria || []).map((criteria, index) => (
                <div key={index} className="flex items-start space-x-4 p-4 bg-muted rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Switch
                        checked={criteria.active !== false}
                        onCheckedChange={(checked) => {
                          const newCriteria = [...(siteConfig.eligibility_criteria || [])];
                          newCriteria[index] = { ...newCriteria[index], active: checked };
                          handleConfigChange('eligibility_criteria', newCriteria);
                        }}
                      />
                      <span className="text-sm text-gray-600">{criteria.active !== false ? 'Active' : 'Inactive'}</span>
                    </div>
                    <Textarea
                      value={criteria.text || ''}
                      onChange={(e) => {
                        const newCriteria = [...(siteConfig.eligibility_criteria || [])];
                        newCriteria[index] = { ...newCriteria[index], text: e.target.value };
                        handleConfigChange('eligibility_criteria', newCriteria);
                      }}
                      rows={2}
                    />
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      const newCriteria = (siteConfig.eligibility_criteria || []).filter((_, i) => i !== index);
                      handleConfigChange('eligibility_criteria', newCriteria);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}

              <Button
                variant="outline"
                onClick={() => {
                  const newCriteria = [...(siteConfig.eligibility_criteria || []), { text: '', active: true }];
                  handleConfigChange('eligibility_criteria', newCriteria);
                }}
                className="w-full"
                data-testid="add-eligibility-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Eligibility Criterion
              </Button>

              <Button onClick={saveSiteConfig} disabled={saving} className="w-full" data-testid="save-eligibility-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Eligibility Criteria'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Aptitude Test Config */}
        {activeSettingsTab === 'aptitude-test' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Test Configuration</CardTitle>
                <CardDescription>Settings applied to every Academic Assessment generated</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <Label>Passages per Test</Label>
                    <Input type="number" min="1" max="20" value={testConfig.passages_per_test}
                      onChange={e => setTestConfig(c => ({ ...c, passages_per_test: parseInt(e.target.value) || 6 }))} />
                    <p className="text-xs text-muted-foreground mt-1">Randomly selected from active passages</p>
                  </div>
                  <div>
                    <Label>Time Limit (minutes)</Label>
                    <Input type="number" min="10" max="180" value={testConfig.time_limit_minutes}
                      onChange={e => setTestConfig(c => ({ ...c, time_limit_minutes: parseInt(e.target.value) || 60 }))} />
                  </div>
                  <div>
                    <Label>Link Expiry (hours)</Label>
                    <Input type="number" min="1" max="168" value={testConfig.link_expiry_hours}
                      onChange={e => setTestConfig(c => ({ ...c, link_expiry_hours: parseInt(e.target.value) || 8 }))} />
                    <p className="text-xs text-muted-foreground mt-1">How long the assessment link stays active after being sent</p>
                  </div>
                </div>
                <Button onClick={handleSaveTestConfig} disabled={savingTestConfig} className="btn-primary">
                  {savingTestConfig ? 'Saving…' : 'Save Configuration'}
                </Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Assessment Date &amp; Time</CardTitle>
                <CardDescription>Set per intake, not globally</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-blue-50 border border-blue-200 rounded p-4 text-sm text-blue-800">
                  <p className="font-semibold mb-1">Test date and time are configured per intake.</p>
                  <p>Go to <strong>Intakes → Pipeline → Step 2</strong> to set the assessment date and time. These are automatically included in the Academic Assessment Details email when test links are sent.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Partnerships Section */}
        {activeSettingsTab === 'partnerships' && (
          <Card>
            <CardHeader>
              <CardTitle>Partnerships & Affiliations</CardTitle>
              <CardDescription>Manage the partnerships section displayed on the landing page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Section Title</Label>
                <Input
                  value={siteConfig.partnerships_title || ''}
                  onChange={e => handleConfigChange('partnerships_title', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Section Description</Label>
                <Textarea
                  rows={3}
                  value={siteConfig.partnerships_description || ''}
                  onChange={e => handleConfigChange('partnerships_description', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Partnership Categories</Label>
                <p className="text-xs text-muted-foreground">Each line is displayed as a separate category item</p>
                <Textarea
                  rows={8}
                  value={(siteConfig.partnerships_categories || []).join('\n')}
                  onChange={e => handleConfigChange('partnerships_categories', e.target.value.split('\n').filter(l => l.trim()))}
                  placeholder={"International law firms\nLegal technology companies\nAcademic institutions"}
                />
              </div>
              <Button onClick={saveSiteConfig} disabled={saving} className="w-full">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Partnerships'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Declarations Management */}
        {activeSettingsTab === 'declarations' && (
          <Card>
            <CardHeader>
              <CardTitle>Declarations & Undertakings</CardTitle>
              <CardDescription>Manage the declarations applicants must agree to when submitting their application</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                <h3 className="font-bold text-blue-900 mb-2">How it works</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Each declaration appears as a checkbox on the application form</li>
                  <li>• <strong>Mandatory</strong> declarations must be checked to submit</li>
                  <li>• <strong>Optional</strong> declarations can be left unchecked</li>
                  <li>• You can add links within declarations (e.g., to Terms & Conditions)</li>
                </ul>
              </div>
              
              <div className="space-y-4">
                {(siteConfig.declarations || []).map((declaration, index) => (
                  <div key={index} className="bg-muted p-4 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-primary">Declaration {index + 1}</span>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <Switch
                            checked={declaration.mandatory !== false}
                            onCheckedChange={(checked) => {
                              const newDeclarations = [...(siteConfig.declarations || [])];
                              newDeclarations[index] = { ...newDeclarations[index], mandatory: checked };
                              handleConfigChange('declarations', newDeclarations);
                            }}
                          />
                          <span className={`text-sm ${declaration.mandatory !== false ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                            {declaration.mandatory !== false ? 'Mandatory' : 'Optional'}
                          </span>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            const newDeclarations = (siteConfig.declarations || []).filter((_, i) => i !== index);
                            handleConfigChange('declarations', newDeclarations);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-sm">Declaration Text</Label>
                      <Textarea
                        value={declaration.text || ''}
                        onChange={(e) => {
                          const newDeclarations = [...(siteConfig.declarations || [])];
                          newDeclarations[index] = { ...newDeclarations[index], text: e.target.value };
                          handleConfigChange('declarations', newDeclarations);
                        }}
                        rows={3}
                        placeholder="Enter the declaration text..."
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={declaration.has_link || false}
                        onChange={(e) => {
                          const newDeclarations = [...(siteConfig.declarations || [])];
                          newDeclarations[index] = { ...newDeclarations[index], has_link: e.target.checked };
                          handleConfigChange('declarations', newDeclarations);
                        }}
                        className="rounded"
                      />
                      <Label className="text-sm">Include a link in this declaration</Label>
                    </div>

                    {declaration.has_link && (
                      <div className="grid md:grid-cols-2 gap-3 pl-6 border-l-2 border-blue-200">
                        <div>
                          <Label className="text-sm">Link Text (displayed in declaration)</Label>
                          <Input
                            value={declaration.link_text || ''}
                            onChange={(e) => {
                              const newDeclarations = [...(siteConfig.declarations || [])];
                              newDeclarations[index] = { ...newDeclarations[index], link_text: e.target.value };
                              handleConfigChange('declarations', newDeclarations);
                            }}
                            placeholder="e.g., Terms and Conditions"
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Link URL</Label>
                          <Input
                            value={declaration.link_url || ''}
                            onChange={(e) => {
                              const newDeclarations = [...(siteConfig.declarations || [])];
                              newDeclarations[index] = { ...newDeclarations[index], link_url: e.target.value };
                              handleConfigChange('declarations', newDeclarations);
                            }}
                            placeholder="e.g., /terms-and-conditions"
                          />
                        </div>
                      </div>
                    )}

                    <div>
                      <Label className="text-sm">Display Order</Label>
                      <Input
                        type="number"
                        value={declaration.order || index + 1}
                        onChange={(e) => {
                          const newDeclarations = [...(siteConfig.declarations || [])];
                          newDeclarations[index] = { ...newDeclarations[index], order: parseInt(e.target.value) || index + 1 };
                          handleConfigChange('declarations', newDeclarations);
                        }}
                        className="w-24"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <Button
                variant="outline"
                onClick={() => {
                  const newDeclarations = [...(siteConfig.declarations || []), { 
                    id: `declaration_${Date.now()}`,
                    text: '', 
                    mandatory: true, 
                    order: (siteConfig.declarations || []).length + 1,
                    has_link: false
                  }];
                  handleConfigChange('declarations', newDeclarations);
                }}
                className="w-full"
                data-testid="add-declaration-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Declaration
              </Button>

              <Button onClick={saveSiteConfig} disabled={saving} className="w-full" data-testid="save-declarations-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Declarations'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Terms & Conditions */}
        {activeSettingsTab === 'terms' && (
          <Card>
            <CardHeader>
              <CardTitle>Terms & Conditions</CardTitle>
              <CardDescription>Edit the Terms and Conditions page content (supports Markdown formatting)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                <h3 className="font-bold text-blue-900 mb-2">Markdown Tips</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li><code># Heading 1</code>, <code>## Heading 2</code>, <code>### Heading 3</code></li>
                  <li><code>**bold text**</code>, <code>*italic text*</code></li>
                  <li><code>- item</code> for bullet points</li>
                  <li><code>1. item</code> for numbered lists</li>
                  <li><code>---</code> for horizontal line</li>
                </ul>
              </div>

              <div className="space-y-2">
                <Label>Terms & Conditions Content</Label>
                <Textarea 
                  value={siteConfig.terms_and_conditions || ''}
                  onChange={(e) => handleConfigChange('terms_and_conditions', e.target.value)}
                  rows={20}
                  className="font-mono text-sm"
                  placeholder="# Terms and Conditions..."
                  data-testid="terms-content-input"
                />
              </div>

              <div className="flex gap-4">
                <Button 
                  variant="outline" 
                  onClick={() => window.open('/terms-and-conditions', '_blank')}
                  className="flex-1"
                >
                  Preview Terms Page
                </Button>
                <Button onClick={saveSiteConfig} disabled={saving} className="flex-1" data-testid="save-terms-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Terms & Conditions'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Admission Settings */}
        {activeSettingsTab === 'admissions' && (
          <Card>
            <CardHeader>
              <CardTitle>Admission Settings</CardTitle>
              <CardDescription>Control whether applications are open and customize the closed message</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <h3 className="font-bold text-lg">Admissions Status</h3>
                  <p className="text-sm text-gray-600">Toggle to open or close admissions</p>
                </div>
                <div className="flex items-center space-x-4">
                  <span className={admissionSettings.admissions_open ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                    {admissionSettings.admissions_open ? 'OPEN' : 'CLOSED'}
                  </span>
                  <Switch
                    checked={admissionSettings.admissions_open}
                    onCheckedChange={(checked) => setAdmissionSettings(prev => ({ ...prev, admissions_open: checked }))}
                    data-testid="admissions-toggle"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Closed Message (shown when admissions are closed)</Label>
                <Textarea
                  value={admissionSettings.closed_message || ''}
                  onChange={(e) => setAdmissionSettings(prev => ({ ...prev, closed_message: e.target.value }))}
                  placeholder="Admissions are currently closed. Please check back later for the next intake period."
                  rows={4}
                  data-testid="closed-message-input"
                />
                <p className="text-sm text-gray-500">
                  Tip: Include dates for the next intake if available (e.g., "Applications for the January 2026 cohort will open on November 1, 2025")
                </p>
              </div>

              <p className="text-xs text-muted-foreground border-t pt-3">Eligibility criteria (max age, min GPA, min LLB year) and scoring weights are now configured per intake period under <strong>Intake Periods → Edit</strong>.</p>

              <Button onClick={saveAdmissionSettings} disabled={saving} className="w-full" data-testid="save-admission-settings-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Admission Settings'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Email Templates */}
        {activeSettingsTab === 'emails' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Email Templates</CardTitle>
                <CardDescription>Customize email content sent to applicants. Use placeholders like {'{{applicant_name}}'} for dynamic content.</CardDescription>
              </div>
              <Button variant="outline" onClick={initializeTemplates} data-testid="init-templates-btn">
                <RefreshCw className="w-4 h-4 mr-2" />
                Initialize Defaults
              </Button>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="space-y-2">
                {emailTemplates.map((template) => (
                  <AccordionItem key={template.id} value={template.id} className="border rounded-lg px-4">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center space-x-4">
                        <Mail className="w-4 h-4 text-secondary" />
                        <div className="text-left">
                          <div className="font-medium">{template.name}</div>
                          <div className="text-sm text-gray-500">{template.template_key}</div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pt-4 space-y-4">
                        <div>
                          <Label className="text-sm text-gray-600">Subject</Label>
                          <p className="font-medium">{template.subject}</p>
                        </div>
                        <div>
                          <Label className="text-sm text-gray-600">Available Placeholders</Label>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {(template.placeholders || []).map((p, idx) => (
                              <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                {`{{${p}}}`}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => openTemplateDialog(template)} data-testid={`edit-template-${template.template_key}`}>
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit Template
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => { setPreviewTemplate(template); setPreviewDialog(true); }}>
                            Preview
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => deleteTemplate(template.id)}>
                            Delete
                          </Button>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              {emailTemplates.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No email templates yet. Click "Initialize Defaults" to create default email templates.
                </div>
              )}
            </CardContent>
          </Card>
        )}

      {/* FAQ Dialog */}
      <Dialog open={faqDialogOpen} onOpenChange={setFaqDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingFaq ? 'Edit FAQ' : 'Add New FAQ'}</DialogTitle>
            <DialogDescription>Enter the question and answer</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Question</Label>
              <Input 
                value={faqForm.question}
                onChange={(e) => setFaqForm(prev => ({ ...prev, question: e.target.value }))}
                placeholder="Enter the question..."
              />
            </div>
            <div>
              <Label>Answer</Label>
              <Textarea 
                value={faqForm.answer}
                onChange={(e) => setFaqForm(prev => ({ ...prev, answer: e.target.value }))}
                placeholder="Enter the answer..."
                rows={4}
              />
            </div>
            <div>
              <Label>Display Order</Label>
              <Input 
                type="number"
                value={faqForm.order}
                onChange={(e) => setFaqForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFaqDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveFaq} disabled={saving}>
              {saving ? 'Saving...' : 'Save FAQ'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Template Dialog */}
      {/* ── Scoring Weights Tab ──────────────────────────────────────── */}
        {activeSettingsTab === 'scoring' && (
        <div className="space-y-6">

          {/* ── Application Score config ── */}
          <Card>
            <CardHeader>
              <CardTitle>Application Score Weights</CardTitle>
              <CardDescription>
                Academic score calculated at submission time for ranking and shortlisting. Must sum to 100%.
              </CardDescription>
            </CardHeader>
            <CardContent className="max-w-md space-y-4">
              <p className="text-xs text-muted-foreground">Min GPA is the eligibility floor — applicants below it are marked ineligible and score 0 pts. O-Level is no longer scored.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Min GPA (4.0 scale)</Label>
                  <p className="text-xs text-muted-foreground mb-1">Eligibility floor for 4.0-scale applicants</p>
                  <Input type="number" min="0" max="4" step="0.1" value={scoringConfig.gpa_min_4}
                    onChange={e => { setScoringError(''); setScoringConfig(c => ({ ...c, gpa_min_4: parseFloat(e.target.value) || 0 })); }} />
                </div>
                <div>
                  <Label>Min GPA (5.0 scale)</Label>
                  <p className="text-xs text-muted-foreground mb-1">Eligibility floor for 5.0-scale applicants</p>
                  <Input type="number" min="0" max="5" step="0.1" value={scoringConfig.gpa_min_5}
                    onChange={e => { setScoringError(''); setScoringConfig(c => ({ ...c, gpa_min_5: parseFloat(e.target.value) || 0 })); }} />
                </div>
              </div>
              <div>
                <Label>LLB GPA Weight (%)</Label>
                <p className="text-xs text-muted-foreground mb-1">Linear scale from scale-min GPA (0 pts) to scale-max GPA (full weight)</p>
                <Input type="number" min="0" max="100" step="1" value={scoringConfig.gpa_weight}
                  onChange={e => { setScoringError(''); setScoringConfig(c => ({ ...c, gpa_weight: parseFloat(e.target.value) || 0 })); }} />
              </div>
              <div>
                <Label>A-Level Weight (%)</Label>
                <p className="text-xs text-muted-foreground mb-1">Top 3 subject grades (A=6, B=5, C=4, D=3, E=2, S=1, F=0)</p>
                <Input type="number" min="0" max="100" step="1" value={scoringConfig.a_level_weight}
                  onChange={e => { setScoringError(''); setScoringConfig(c => ({ ...c, a_level_weight: parseFloat(e.target.value) || 0 })); }} />
              </div>
              {(() => {
                const total = (scoringConfig.gpa_weight || 0) + (scoringConfig.a_level_weight || 0);
                const ok = Math.abs(total - 100) < 0.01;
                return <div className={`flex items-center justify-between p-3 rounded border ${ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <span className="text-sm font-medium">Total</span>
                  <span className={`font-bold ${ok ? 'text-green-700' : 'text-red-600'}`}>{total.toFixed(0)}%</span>
                </div>;
              })()}
              {scoringError && <p className="text-red-600 text-sm">{scoringError}</p>}
              <Button onClick={async () => {
                const total = (scoringConfig.gpa_weight || 0) + (scoringConfig.a_level_weight || 0);
                if (Math.abs(total - 100) > 0.01) { setScoringError(`Must sum to 100 (current: ${total.toFixed(1)}%)`); return; }
                setSaving(true);
                try {
                  const token = localStorage.getItem('token');
                  await axios.put(`${API}/scoring-config`, { gpa_weight: scoringConfig.gpa_weight, a_level_weight: scoringConfig.a_level_weight, gpa_min_4: scoringConfig.gpa_min_4, gpa_min_5: scoringConfig.gpa_min_5 }, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success('Application score weights saved');
                } catch (err) { setScoringError(err.response?.data?.detail || 'Failed to save'); } finally { setSaving(false); }
              }} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Application Weights'}
              </Button>
              <div className="border-t pt-4 mt-2 space-y-2">
                <p className="text-xs text-muted-foreground">
                  Saved weights apply to <strong>new submissions only</strong>. Use the button below to retroactively recalculate scores for all existing applications using the current config.
                </p>
                <Button
                  variant="outline"
                  disabled={recalculating}
                  onClick={async () => {
                    if (!window.confirm('Recalculate scores for ALL existing applications using the current scoring config? This cannot be undone.')) return;
                    setRecalculating(true);
                    try {
                      const token = localStorage.getItem('token');
                      const res = await axios.post(`${API}/applications/recalculate-scores`, {}, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success(`Scores recalculated: ${res.data.updated} updated${res.data.failed ? `, ${res.data.failed} failed` : ''}`);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Recalculation failed');
                    } finally {
                      setRecalculating(false);
                    }
                  }}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${recalculating ? 'animate-spin' : ''}`} />
                  {recalculating ? 'Recalculating…' : 'Recalculate All Existing Scores'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* ── Score formula reference ── */}
          <Card className="border-blue-100 bg-blue-50/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-blue-800">How the Application Score is Calculated</CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-blue-900 space-y-4">
              <div>
                <p className="font-semibold mb-1">LLB GPA — linear interpolation, bounds set by applicant's scale (4.0 or 5.0):</p>
                <code className="block bg-white/70 rounded px-3 py-2 font-mono text-xs border border-blue-100">
                  gpa_pts = ( (GPA − scale_min) / (scale_max − scale_min) ) × gpa_weight
                </code>
                <p className="mt-1 text-blue-700">4.0 scale: min={scoringConfig.gpa_min_4}, max=4.0 &nbsp;|&nbsp; 5.0 scale: min={scoringConfig.gpa_min_5}, max=5.0 &nbsp;|&nbsp; Min GPA is the eligibility floor (scores 0 pts); max GPA scores full weight.</p>
              </div>
              <div>
                <p className="font-semibold mb-1">A-Level — top 3 subject grade points (system picks highest), scaled to weight:</p>
                <code className="block bg-white/70 rounded px-3 py-2 font-mono text-xs border border-blue-100">
                  a_pts = ( sum(top_3_grade_pts) / 18 ) × a_level_weight
                </code>
                <p className="mt-1 text-blue-700">Grade scale: A=6, B=5, C=4, D=3, E=2, S=1, F=0. Max raw = 3 × 6 = 18.</p>
              </div>
              <div className="border-t border-blue-200 pt-3">
                <p className="font-semibold">Total application score = gpa_pts + a_pts (max = 100 pts)</p>
              </div>
            </CardContent>
          </Card>

          {/* ── Final Score config ── */}
          <Card>
            <CardHeader>
              <CardTitle>Final Score Weights</CardTitle>
              <CardDescription>
                Combined score calculated after aptitude test completion. Includes all academic components plus aptitude result. Must sum to 100%.
              </CardDescription>
            </CardHeader>
            <CardContent className="max-w-md space-y-4">
              <p className="text-xs text-muted-foreground">GPA + A-Level + Aptitude must sum to 100. O-Level is not included in final scoring.</p>
              <div>
                <Label>LLB GPA Weight (%)</Label>
                <Input type="number" min="0" max="100" step="1" value={finalScoringConfig.final_gpa_weight}
                  onChange={e => { setFinalScoringError(''); setFinalScoringConfig(c => ({ ...c, final_gpa_weight: parseFloat(e.target.value) || 0 })); }} />
              </div>
              <div>
                <Label>A-Level Weight (%)</Label>
                <Input type="number" min="0" max="100" step="1" value={finalScoringConfig.final_a_level_weight}
                  onChange={e => { setFinalScoringError(''); setFinalScoringConfig(c => ({ ...c, final_a_level_weight: parseFloat(e.target.value) || 0 })); }} />
              </div>
              <div>
                <Label>Academic Assessment Weight (%)</Label>
                <p className="text-xs text-muted-foreground mb-1">MCQ score 0–100% from the Academic Assessment</p>
                <Input type="number" min="0" max="100" step="1" value={finalScoringConfig.final_aptitude_weight}
                  onChange={e => { setFinalScoringError(''); setFinalScoringConfig(c => ({ ...c, final_aptitude_weight: parseFloat(e.target.value) || 0 })); }} />
              </div>
              {(() => {
                const total = (finalScoringConfig.final_gpa_weight || 0) + (finalScoringConfig.final_a_level_weight || 0) + (finalScoringConfig.final_aptitude_weight || 0);
                const ok = Math.abs(total - 100) < 0.01;
                return <div className={`flex items-center justify-between p-3 rounded border ${ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <span className="text-sm font-medium">Total</span>
                  <span className={`font-bold ${ok ? 'text-green-700' : 'text-red-600'}`}>{total.toFixed(0)}%</span>
                </div>;
              })()}
              {finalScoringError && <p className="text-red-600 text-sm">{finalScoringError}</p>}
              <Button onClick={async () => {
                const total = (finalScoringConfig.final_gpa_weight || 0) + (finalScoringConfig.final_a_level_weight || 0) + (finalScoringConfig.final_aptitude_weight || 0);
                if (Math.abs(total - 100) > 0.01) { setFinalScoringError(`Must sum to 100 (current: ${total.toFixed(1)}%)`); return; }
                setSaving(true);
                try {
                  const token = localStorage.getItem('token');
                  await axios.put(`${API}/scoring-config`, { final_gpa_weight: finalScoringConfig.final_gpa_weight, final_a_level_weight: finalScoringConfig.final_a_level_weight, final_aptitude_weight: finalScoringConfig.final_aptitude_weight }, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success('Final score weights saved');
                } catch (err) { setFinalScoringError(err.response?.data?.detail || 'Failed to save'); } finally { setSaving(false); }
              }} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Final Score Weights'}
              </Button>
              <p className="text-xs text-muted-foreground">Final score is recalculated automatically when an applicant's aptitude test is submitted.</p>
            </CardContent>
          </Card>

        </div>
        )}

      {/* ── Review Checklist Tab ─────────────────────────────────────────── */}
        {activeSettingsTab === 'checklist' && (
        <div className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-primary" /> Review Checklist Configuration
                </CardTitle>
                <CardDescription className="mt-1">
                  Customise the sections and items shown in the inline document reviewer. Changes apply immediately to all future reviews.
                </CardDescription>
              </div>
              <Button onClick={saveChecklistConfig} disabled={checklistSaving} className="shrink-0">
                <Save className="w-4 h-4 mr-1.5" />{checklistSaving ? 'Saving…' : 'Save Changes'}
              </Button>
            </CardHeader>
            <CardContent>
              {checklistLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : (
                <div className="space-y-3">
                  {checklistSections.map((section, sIdx) => (
                    <div key={section.id} className="border rounded-lg overflow-hidden">
                      {/* Section header */}
                      <div className="bg-gray-50 px-4 py-3 flex items-center gap-3 border-b">
                        <div className="flex flex-col gap-0.5 shrink-0">
                          <button onClick={() => clMoveSectionUp(sIdx)} disabled={sIdx === 0} className="text-gray-400 hover:text-gray-600 disabled:opacity-20 leading-none">▲</button>
                          <button onClick={() => clMoveSectionDown(sIdx)} disabled={sIdx === checklistSections.length - 1} className="text-gray-400 hover:text-gray-600 disabled:opacity-20 leading-none">▼</button>
                        </div>
                        <GripVertical className="w-4 h-4 text-gray-300 shrink-0" />
                        <Input
                          value={section.label}
                          onChange={e => clUpdateSectionLabel(sIdx, e.target.value)}
                          className="font-semibold text-sm h-8 flex-1"
                          placeholder="Section name"
                        />
                        <div className="flex items-center gap-2 shrink-0">
                          <label className="text-xs text-muted-foreground whitespace-nowrap">Linked doc tab</label>
                          <select
                            value={section.doc_idx ?? ''}
                            onChange={e => clUpdateDocIdx(sIdx, e.target.value)}
                            className="border rounded px-2 py-1 text-xs bg-white"
                          >
                            <option value="">None</option>
                            <option value="0">Tab 0 (NIDA/Passport)</option>
                            <option value="1">Tab 1 (O-Level)</option>
                            <option value="2">Tab 2 (A-Level)</option>
                            <option value="3">Tab 3 (LLB)</option>
                          </select>
                        </div>
                        <button onClick={() => clRemoveSection(sIdx)} className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors shrink-0">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Items */}
                      <div className="divide-y">
                        {section.items.length === 0 && (
                          <p className="text-xs text-muted-foreground italic px-4 py-3">No items yet. Add one below.</p>
                        )}
                        {section.items.map((item, iIdx) => (
                          <div key={item.key} className="flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50/50">
                            <div className="flex flex-col gap-0.5 shrink-0">
                              <button onClick={() => clMoveItemUp(sIdx, iIdx)} disabled={iIdx === 0} className="text-gray-300 hover:text-gray-500 disabled:opacity-20 leading-none text-xs">▲</button>
                              <button onClick={() => clMoveItemDown(sIdx, iIdx)} disabled={iIdx === section.items.length - 1} className="text-gray-300 hover:text-gray-500 disabled:opacity-20 leading-none text-xs">▼</button>
                            </div>
                            <CheckSquare className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                            <Input
                              value={item.label}
                              onChange={e => clUpdateItemLabel(sIdx, iIdx, e.target.value)}
                              className="text-sm h-8 flex-1"
                              placeholder="Item label"
                            />
                            <span className="text-xs text-gray-300 font-mono shrink-0 hidden sm:block">{item.key}</span>
                            <button onClick={() => clRemoveItem(sIdx, iIdx)} className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors shrink-0">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* Add item */}
                      <div className="px-4 py-2 border-t bg-gray-50/50">
                        <button onClick={() => clAddItem(sIdx)} className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 font-medium">
                          <Plus className="w-3.5 h-3.5" /> Add item
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Add section */}
                  <button
                    onClick={clAddSection}
                    className="w-full border-2 border-dashed border-gray-200 hover:border-primary/40 hover:bg-primary/5 rounded-lg py-3 text-sm text-muted-foreground hover:text-primary flex items-center justify-center gap-2 transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Add Section
                  </button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Key reference note */}
          <Card className="border-blue-100 bg-blue-50">
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-blue-700">
                <strong>Note:</strong> Each item has an internal key (shown in grey). Keys are auto-generated when items are created and never change — this ensures that previously saved checklist progress on applications is preserved even if you rename the item label.
              </p>
            </CardContent>
          </Card>
        </div>
        )}

      {/* ── Passage Bank Tab ──────────────────────────────────────────────── */}
        {activeSettingsTab === 'questions' && (
        <div className="space-y-6">

          {/* Passage Bank */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Passage Bank</CardTitle>
                  <CardDescription>{passages.length} passages · {passages.filter(p => p.active).length} active</CardDescription>
                </div>
                <Button className="btn-primary gap-2" onClick={() => { setPassageForm({ id: null, title: '', text: '', source: '' }); setPassageDialog(true); }}>
                  <Plus className="w-4 h-4" /> Add Passage
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingPassages ? (
                <div className="text-center py-8 text-muted-foreground">Loading passages…</div>
              ) : passages.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No passages yet. Add your first passage to build the question bank.</div>
              ) : (
                <div className="space-y-3">
                  {passages.map((passage, idx) => (
                    <div key={passage.id} className="border rounded-lg overflow-hidden">
                      {/* Passage header row */}
                      <div className="flex items-center gap-3 p-4 bg-white hover:bg-muted/30 transition-colors">
                        <button
                          onClick={() => handleTogglePassage(passage.id)}
                          className="flex-1 flex items-center gap-3 text-left"
                        >
                          <span className="text-xs font-bold text-muted-foreground w-6">{idx + 1}</span>
                          <div className="flex-1">
                            <p className="font-medium text-primary text-sm">{passage.title}</p>
                            {passage.source && <p className="text-xs text-muted-foreground">{passage.source}</p>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${passage.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {passage.active ? 'Active' : 'Inactive'}
                            </span>
                            <span className="text-xs text-muted-foreground">{passage.question_count || 0} Q</span>
                            <span className="text-xs text-muted-foreground">{passage.text?.split(/\s+/).length || 0} words</span>
                          </div>
                        </button>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => { setPassageForm({ id: passage.id, title: passage.title, text: passage.text, source: passage.source || '' }); setPassageDialog(true); }}>
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleTogglePassageActive(passage.id, passage.active)}>
                            {passage.active ? <span className="text-xs text-amber-600">Disable</span> : <span className="text-xs text-green-600">Enable</span>}
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDeletePassage(passage.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>

                      {/* Expanded: questions */}
                      {expandedPassage === passage.id && (
                        <div className="border-t bg-muted/20 p-4 space-y-3">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-primary">Questions for this passage</h4>
                            <Button size="sm" className="btn-primary gap-1 text-xs" onClick={() => {
                              setQuestionForm({ id: null, passage_id: passage.id, text: '', options: ['', '', '', '', ''], correct_answer: 'A', question_type: '' });
                              setQuestionDialog(true);
                            }}>
                              <Plus className="w-3 h-3" /> Add Question
                            </Button>
                          </div>
                          {!passageQuestions[passage.id] ? (
                            <div className="text-sm text-muted-foreground">Loading…</div>
                          ) : passageQuestions[passage.id].length === 0 ? (
                            <div className="text-sm text-muted-foreground">No questions yet for this passage.</div>
                          ) : (
                            <div className="space-y-2">
                              {passageQuestions[passage.id].map((q, qi) => (
                                <div key={q.id} className="bg-white rounded border p-3 flex items-start gap-3">
                                  <div className="flex flex-col items-center gap-0.5 shrink-0">
                                    <button onClick={() => moveQuestion(passage.id, qi, -1)} disabled={qi === 0}
                                      className="p-0.5 rounded hover:bg-muted disabled:opacity-20 disabled:cursor-not-allowed">
                                      <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
                                    </button>
                                    <span className="text-xs font-bold text-secondary">Q{qi + 1}</span>
                                    <button onClick={() => moveQuestion(passage.id, qi, 1)} disabled={qi === passageQuestions[passage.id].length - 1}
                                      className="p-0.5 rounded hover:bg-muted disabled:opacity-20 disabled:cursor-not-allowed">
                                      <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                                    </button>
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-primary mb-1">{q.text}</p>
                                    <div className="grid grid-cols-2 gap-1">
                                      {q.options.map((opt, oi) => (
                                        <p key={oi} className={`text-xs px-2 py-0.5 rounded ${['A','B','C','D','E'][oi] === q.correct_answer ? 'bg-green-100 text-green-700 font-semibold' : 'text-muted-foreground'}`}>
                                          {['A','B','C','D','E'][oi]}. {opt}
                                        </p>
                                      ))}
                                    </div>
                                    {q.question_type && <span className="text-xs text-muted-foreground mt-1 block">{q.question_type}</span>}
                                  </div>
                                  <div className="flex gap-1">
                                    <Button size="sm" variant="ghost" onClick={() => {
                                      setQuestionForm({ id: q.id, passage_id: passage.id, text: q.text, options: [...q.options, '', '', '', '', ''].slice(0, 5), correct_answer: q.correct_answer, question_type: q.question_type || '' });
                                      setQuestionDialog(true);
                                    }}><Edit2 className="w-3 h-3" /></Button>
                                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteQuestion(q.id, passage.id)}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Passage Dialog */}
          {passageDialog && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mt-8 mb-8">
                <div className="p-6 border-b flex items-center justify-between">
                  <h2 className="text-xl font-bold text-primary">{passageForm.id ? 'Edit Passage' : 'Add New Passage'}</h2>
                  <button onClick={() => setPassageDialog(false)} className="text-muted-foreground hover:text-primary">✕</button>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <Label>Passage Title</Label>
                    <Input value={passageForm.title} onChange={e => setPassageForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. The Genius of Julius Nyerere" />
                  </div>
                  <div>
                    <Label>Source / Attribution (optional)</Label>
                    <Input value={passageForm.source} onChange={e => setPassageForm(f => ({ ...f, source: e.target.value }))} placeholder="e.g. UNESCO, World Heritage Convention" />
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <Label>Passage Text</Label>
                      <span className={`text-xs ${passageForm.text.split(/\s+/).filter(Boolean).length < 450 || passageForm.text.split(/\s+/).filter(Boolean).length > 650 ? 'text-amber-600' : 'text-green-600'}`}>
                        {passageForm.text.split(/\s+/).filter(Boolean).length} words (target: 450–650)
                      </span>
                    </div>
                    <textarea
                      className="w-full border rounded-md p-3 text-sm min-h-64 resize-y focus:outline-none focus:ring-2 focus:ring-primary/30"
                      value={passageForm.text}
                      onChange={e => setPassageForm(f => ({ ...f, text: e.target.value }))}
                      placeholder="Paste passage text here (450-650 words)..."
                    />
                  </div>
                </div>
                <div className="p-6 border-t flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setPassageDialog(false)}>Cancel</Button>
                  <Button className="btn-primary" onClick={handleSavePassage} disabled={savingPassage}>
                    {savingPassage ? 'Saving…' : passageForm.id ? 'Update Passage' : 'Add Passage'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Question Dialog */}
          {questionDialog && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mt-8 mb-8">
                <div className="p-6 border-b flex items-center justify-between">
                  <h2 className="text-xl font-bold text-primary">{questionForm.id ? 'Edit Question' : 'Add Question'}</h2>
                  <button onClick={() => setQuestionDialog(false)} className="text-muted-foreground hover:text-primary">✕</button>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <Label>Question Text</Label>
                    <textarea
                      className="w-full border rounded-md p-3 text-sm min-h-20 resize-y focus:outline-none focus:ring-2 focus:ring-primary/30"
                      value={questionForm.text}
                      onChange={e => setQuestionForm(f => ({ ...f, text: e.target.value }))}
                      placeholder="Which of the following best describes..."
                    />
                  </div>
                  <div>
                    <Label>Question Type</Label>
                    <select
                      className="w-full border rounded-md p-2 text-sm"
                      value={questionForm.question_type}
                      onChange={e => setQuestionForm(f => ({ ...f, question_type: e.target.value }))}
                    >
                      <option value="">— Select type —</option>
                      <option value="main_argument">Main argument / purpose</option>
                      <option value="supported_statement">Statement most supported</option>
                      <option value="inference">Drawing valid inferences</option>
                      <option value="assumption">Recognising assumptions</option>
                      <option value="language">Interpreting language in context</option>
                      <option value="comparison">Distinguishing stronger/weaker claims</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Answer Options (A–E)</Label>
                    {questionForm.options.map((opt, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold border-2 cursor-pointer ${questionForm.correct_answer === ['A','B','C','D','E'][i] ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 text-gray-400'}`}
                          onClick={() => setQuestionForm(f => ({ ...f, correct_answer: ['A','B','C','D','E'][i] }))}
                          title="Click to mark as correct answer"
                        >
                          {['A','B','C','D','E'][i]}
                        </span>
                        <Input
                          value={opt}
                          onChange={e => {
                            const newOpts = [...questionForm.options];
                            newOpts[i] = e.target.value;
                            setQuestionForm(f => ({ ...f, options: newOpts }));
                          }}
                          placeholder={`Option ${['A','B','C','D','E'][i]}`}
                          className="flex-1"
                        />
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground">Click the letter circle to mark the correct answer (highlighted green)</p>
                  </div>
                </div>
                <div className="p-6 border-t flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setQuestionDialog(false)}>Cancel</Button>
                  <Button className="btn-primary" onClick={handleSaveQuestion} disabled={savingQuestion}>
                    {savingQuestion ? 'Saving…' : questionForm.id ? 'Update Question' : 'Add Question'}
                  </Button>
                </div>
              </div>
            </div>
          )}

        </div>
        )}

      {/* ── System Config Tab ──────────────────────────────────────────────── */}
        {activeSettingsTab === 'system' && (
        <Card>
          <CardHeader>
            <CardTitle>System Configuration</CardTitle>
            <CardDescription>
              Email delivery credentials and environment settings. Changes take effect immediately — no server restart needed.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 max-w-xl">

            {/* Email Provider API Key */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Email Provider API Key</Label>
              <div className="flex items-center gap-3 mb-1">
                {systemConfig.smtp2go_configured ? (
                  <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded font-medium">✓ Configured — {systemConfig.smtp2go_key_preview}</span>
                ) : (
                  <span className="text-xs text-red-700 bg-red-100 px-2 py-0.5 rounded font-medium">✗ Not configured — emails will not be sent</span>
                )}
              </div>
              <PasswordInput
                placeholder="Enter new key to replace existing (leave blank to keep current)"
                value={systemConfigForm.smtp2go_api_key}
                onChange={e => setSystemConfigForm(f => ({ ...f, smtp2go_api_key: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">The full key is never shown. Enter a new value only if you want to replace the current key.</p>
            </div>

            {/* Sender Name */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Sender Name</Label>
              <Input
                placeholder="FB Attorneys Academy"
                value={systemConfigForm.sender_name}
                onChange={e => setSystemConfigForm(f => ({ ...f, sender_name: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">The display name shown as the email sender (e.g. "FB Attorneys Academy").</p>
            </div>

            {/* Sender Email */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Sender Email</Label>
              <Input
                type="email"
                placeholder="noreply@yourdomain.com"
                value={systemConfigForm.sender_email}
                onChange={e => setSystemConfigForm(f => ({ ...f, sender_email: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">The "From" address on all outgoing emails. Must be verified with your email provider.</p>
            </div>

            {/* Frontend URL */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Frontend URL</Label>
              <Input
                type="url"
                placeholder="https://yourdomain.com"
                value={systemConfigForm.frontend_url}
                onChange={e => setSystemConfigForm(f => ({ ...f, frontend_url: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">Used to build links inside emails (aptitude test link, student portal link). Set this to your live URL in production.</p>
            </div>

            <Button
              onClick={async () => {
                setSavingSystem(true);
                try {
                  const token = localStorage.getItem('token');
                  await axios.put(`${API}/system-config`, systemConfigForm, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  toast.success('System config saved — changes are live immediately');
                  // Refresh the status display
                  const res = await axios.get(`${API}/system-config`, { headers: { Authorization: `Bearer ${token}` } });
                  setSystemConfig(res.data);
                  setSystemConfigForm(f => ({ ...f, smtp2go_api_key: '' })); // clear key field after save
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to save system config');
                } finally {
                  setSavingSystem(false);
                }
              }}
              disabled={savingSystem}
            >
              <Save className="w-4 h-4 mr-2" />
              {savingSystem ? 'Saving...' : 'Save System Config'}
            </Button>

            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800 space-y-1">
              <p className="font-semibold">Security note</p>
              <p>These values are stored securely in the database and never exposed publicly. The API key is never returned to the browser after saving.</p>
            </div>
          </CardContent>
        </Card>
        )}

      {/* ── Dev Tools Tab ───────────────────────────────────────────────────── */}
        {activeSettingsTab === 'devtools' && (
        <Card className="border-orange-200">
          <CardHeader className="bg-orange-50 rounded-t-lg">
            <CardTitle className="text-orange-800">Developer Tools</CardTitle>
            <CardDescription className="text-orange-700">
              Testing utilities. Remove this tab before going to production.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">

            {/* ── Intake Selector ─────────────────────────────────── */}
            <div className="flex items-center gap-3 p-3 bg-orange-50 rounded border border-orange-200">
              <span className="text-sm font-medium text-orange-800 whitespace-nowrap">Intake:</span>
              <select
                className="flex-1 border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
                value={devIntakeId}
                onChange={e => { setDevIntakeId(e.target.value); setTestInvitedApps([]); }}
              >
                <option value="">— select intake —</option>
                {intakesList.map(i => (
                  <option key={i.id} value={i.id}>{i.name} ({i.status})</option>
                ))}
              </select>
              <span className="text-xs text-orange-600">Required for seeding and simulate</span>
            </div>

            {/* ── Toolbar ─────────────────────────────────────────── */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Switch id="seed-force" checked={seedForce} onCheckedChange={setSeedForce} />
                <label htmlFor="seed-force" className="text-sm cursor-pointer">
                  <span className="font-medium">Force mode</span>
                  <span className="text-muted-foreground ml-1">— delete existing records first</span>
                </label>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm" variant="outline"
                  onClick={() => setSeedApplicants(DEFAULT_SEED_APPLICANTS.map((a, i) => ({ ...a, _id: i })))}
                  disabled={seedRunning || deleteRunning}
                >
                  <RefreshCw className="w-3 h-3 mr-1" /> Reset to Defaults
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => setSeedApplicants(prev => [
                    ...prev,
                    { _id: Date.now(), full_name: 'New Applicant', email: 'test@pindush.net', date_of_birth: '2001-01-01', nationality: 'Tanzanian', llb_status: 'completed', llb_gpa: 3.5, llb_gpa_scale: 5, subject1_grade: 'B', subject2_grade: 'B', subject3_grade: 'B', target_status: 'received', simulated_score: 78 }
                  ])}
                  disabled={seedRunning || deleteRunning}
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Row
                </Button>
                <Button
                  size="sm"
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  disabled={seedRunning || deleteRunning || seedApplicants.length === 0}
                  onClick={async () => {
                    setSeedRunning(true);
                    setSeedResults(null);
                    try {
                      const token = localStorage.getItem('token');
                      const payload = seedApplicants.map(({ _id, ...a }) => a);
                      const res = await axios.post(`${API}/dev/seed`, { force: seedForce, applicants: payload, intake_id: devIntakeId || null }, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setSeedResults(res.data);
                      const inserted = res.data.results.filter(r => r.status === 'inserted').length;
                      const skipped  = res.data.results.filter(r => r.status === 'skipped').length;
                      toast.success(`Seed complete — ${inserted} inserted, ${skipped} skipped`);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Seed failed');
                    } finally {
                      setSeedRunning(false);
                    }
                  }}
                >
                  {seedRunning ? 'Running...' : `Run Seed (${seedApplicants.length})`}
                </Button>
                <Button
                  size="sm" variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  disabled={deleteRunning || seedRunning}
                  onClick={async () => {
                    if (!window.confirm('Delete all seed applications from the database? This cannot be undone.')) return;
                    setDeleteRunning(true);
                    try {
                      const token = localStorage.getItem('token');
                      const url = devIntakeId ? `${API}/dev/seed?intake_id=${devIntakeId}` : `${API}/dev/seed`;
                      const res = await axios.delete(url, { headers: { Authorization: `Bearer ${token}` } });
                      setSeedResults(null);
                      setTestInvitedApps([]);
                      toast.success(`Deleted ${res.data.deleted} seed application(s)`);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Delete failed');
                    } finally {
                      setDeleteRunning(false);
                    }
                  }}
                >
                  {deleteRunning ? 'Deleting...' : 'Delete All Seed Data'}
                </Button>
              </div>
            </div>

            {/* ── Editable applicants table ────────────────────────── */}
            <div className="overflow-x-auto rounded border">
              <table className="w-full text-xs min-w-[1100px]">
                <thead className="bg-muted text-muted-foreground">
                  <tr>
                    <th className="text-left px-2 py-2 font-medium">Full Name</th>
                    <th className="text-left px-2 py-2 font-medium">Email</th>
                    <th className="text-left px-2 py-2 font-medium">Date of Birth</th>
                    <th className="text-left px-2 py-2 font-medium">Nationality</th>
                    <th className="text-left px-2 py-2 font-medium">LLB Status</th>
                    <th className="text-left px-2 py-2 font-medium">GPA</th>
                    <th className="text-center px-2 py-2 font-medium">Scale</th>
                    <th className="text-center px-2 py-2 font-medium">S1</th>
                    <th className="text-center px-2 py-2 font-medium">S2</th>
                    <th className="text-center px-2 py-2 font-medium">S3</th>
                    <th className="text-left px-2 py-2 font-medium">Target Stage</th>
                    <th className="text-center px-2 py-2 font-medium">Sim Score</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {seedApplicants.map((a, idx) => {
                    const upd = (field, val) => setSeedApplicants(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));
                    const inputCls = "w-full border rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-orange-400";
                    const selCls  = "border rounded px-1 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-orange-400 w-full";
                    return (
                      <tr key={a._id} className="hover:bg-orange-50/30">
                        <td className="px-2 py-1.5"><input className={inputCls} value={a.full_name} onChange={e => upd('full_name', e.target.value)} /></td>
                        <td className="px-2 py-1.5"><input className={inputCls} value={a.email} onChange={e => upd('email', e.target.value)} /></td>
                        <td className="px-2 py-1.5"><input type="date" className={inputCls} value={a.date_of_birth} onChange={e => upd('date_of_birth', e.target.value)} /></td>
                        <td className="px-2 py-1.5"><input className={inputCls} value={a.nationality} onChange={e => upd('nationality', e.target.value)} /></td>
                        <td className="px-2 py-1.5">
                          <select className={selCls} value={a.llb_status} onChange={e => upd('llb_status', e.target.value)}>
                            <option value="completed">Completed</option>
                            <option value="to_be_awarded">To Be Awarded</option>
                          </select>
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" min="0" max="5" step="0.05" className={`${inputCls} w-16`} value={a.llb_gpa} onChange={e => upd('llb_gpa', parseFloat(e.target.value) || 0)} />
                        </td>
                        <td className="px-2 py-1.5">
                          <select className={`${selCls} w-14`} value={a.llb_gpa_scale || 5} onChange={e => upd('llb_gpa_scale', parseInt(e.target.value))}>
                            <option value={4}>4</option>
                            <option value={5}>5</option>
                          </select>
                        </td>
                        {['subject1_grade', 'subject2_grade', 'subject3_grade'].map(f => (
                          <td key={f} className="px-2 py-1.5">
                            <select className={`${selCls} w-14`} value={a[f]} onChange={e => upd(f, e.target.value)}>
                              {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                            </select>
                          </td>
                        ))}
                        <td className="px-2 py-1.5">
                          <select className={selCls} value={a.target_status || 'received'} onChange={e => upd('target_status', e.target.value)}>
                            <option value="received">received</option>
                            <option value="screening">screening</option>
                            <option value="test_invited">test_invited</option>
                            <option value="test_completed">test_completed</option>
                            <option value="accepted">accepted</option>
                          </select>
                        </td>
                        <td className="px-2 py-1.5">
                          <input
                            type="number" min="0" max="100" step="1"
                            className={`${inputCls} w-16`}
                            value={a.simulated_score ?? 78}
                            disabled={!['test_completed', 'accepted'].includes(a.target_status)}
                            onChange={e => upd('simulated_score', parseFloat(e.target.value) || 0)}
                          />
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <div className="flex items-center gap-1 justify-center">
                            <button
                              type="button"
                              title="Seed this applicant only"
                              disabled={seedingRow === a._id || seedRunning || deleteRunning}
                              className="text-green-500 hover:text-green-700 p-0.5 disabled:opacity-40"
                              onClick={async () => {
                                setSeedingRow(a._id);
                                try {
                                  const token = localStorage.getItem('token');
                                  const { _id, ...payload } = a;
                                  const res = await axios.post(`${API}/dev/seed`, { force: seedForce, applicants: [payload], intake_id: devIntakeId || null }, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  const r = res.data.results[0];
                                  if (r.status === 'inserted') {
                                    toast.success(`Seeded: ${r.name} — ${r.eligible ? 'Eligible' : 'Ineligible'}${r.score != null ? `, score ${r.score.toFixed(1)}` : ''}`);
                                  } else {
                                    toast.warning(`Skipped: ${r.name} — ${r.reason}`);
                                  }
                                  setSeedResults(prev => {
                                    const prev_results = prev?.results || [];
                                    const existing = prev_results.findIndex(x => x.email === r.email);
                                    const updated = existing >= 0
                                      ? prev_results.map((x, i) => i === existing ? r : x)
                                      : [...prev_results, r];
                                    return { deleted: (prev?.deleted || 0) + (res.data.deleted || 0), results: updated };
                                  });
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Seed failed');
                                } finally {
                                  setSeedingRow(null);
                                }
                              }}
                            >
                              {seedingRow === a._id
                                ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                : <Play className="w-3.5 h-3.5" />}
                            </button>
                            <button
                              type="button"
                              onClick={() => setSeedApplicants(prev => prev.filter((_, i) => i !== idx))}
                              className="text-red-400 hover:text-red-600 p-0.5"
                              title="Remove row"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {seedApplicants.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-6">No applicants. Click "Add Row" to add one.</p>
              )}
            </div>

            {/* ── Seed results ─────────────────────────────────────── */}
            {seedResults && (
              <div className="space-y-2">
                {seedResults.deleted > 0 && (
                  <p className="text-xs text-orange-700 bg-orange-50 px-3 py-1.5 rounded border border-orange-200">
                    Force mode: {seedResults.deleted} existing record(s) deleted before resubmitting.
                  </p>
                )}
                <div className="rounded border overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-muted text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2">Name</th>
                        <th className="text-left px-3 py-2">Email</th>
                        <th className="text-left px-3 py-2">Result</th>
                        <th className="text-left px-3 py-2">Eligibility</th>
                        <th className="text-right px-3 py-2">Score</th>
                        <th className="text-left px-3 py-2">Final Stage</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {seedResults.results.map((r, i) => (
                        <tr key={i} className={r.status === 'skipped' ? 'bg-gray-50' : ''}>
                          <td className="px-3 py-1.5 font-medium">{r.name}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">{r.email}</td>
                          <td className="px-3 py-1.5">
                            <span className={`px-2 py-0.5 rounded font-medium ${r.status === 'inserted' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                              {r.status}
                            </span>
                          </td>
                          <td className="px-3 py-1.5">
                            {r.status === 'inserted'
                              ? <span className={`px-2 py-0.5 rounded ${r.eligible ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-700'}`}>{r.eligible ? 'Eligible' : 'Ineligible'}</span>
                              : <span className="text-muted-foreground">{r.reason}</span>}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono">{r.score != null ? r.score.toFixed(1) : '—'}</td>
                          <td className="px-3 py-1.5">
                            {r.status === 'inserted'
                              ? <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">{r.final_status}</span>
                              : <span className="text-muted-foreground">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground">Emails sent to <strong>pratik1–10@pindush.net</strong>. Check each inbox to verify email triggers.</p>
              </div>
            )}

            {/* ── Simulate Assessment ──────────────────────────────── */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">Simulate Assessment Score</p>
                  <p className="text-xs text-muted-foreground">Enter a score for any applicant in <code className="bg-gray-100 px-1 rounded">test_invited</code> status — bypasses the actual test.</p>
                </div>
                <Button size="sm" variant="outline" onClick={fetchTestInvited} disabled={loadingTestInvited || !devIntakeId}>
                  {loadingTestInvited ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  {loadingTestInvited ? '' : 'Load Candidates'}
                </Button>
              </div>
              {testInvitedApps.length === 0 && (
                <p className="text-xs text-muted-foreground py-2 text-center">No applicants in test_invited status — select an intake and click Load Candidates.</p>
              )}
              {testInvitedApps.length > 0 && (
                <div className="space-y-2">
                  {testInvitedApps.map(app => (
                    <div key={app.id} className="flex items-center gap-3 p-2.5 bg-purple-50 border border-purple-100 rounded">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{app.full_name}</p>
                        <p className="text-xs text-muted-foreground">{app.email}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <input
                          type="number" min="0" max="100" step="1"
                          className="w-20 border rounded px-2 py-1 text-sm text-center focus:outline-none focus:ring-1 focus:ring-purple-400"
                          value={simScores[app.id] ?? 78}
                          onChange={e => setSimScores(prev => ({ ...prev, [app.id]: parseFloat(e.target.value) || 0 }))}
                          placeholder="Score %"
                        />
                        <span className="text-xs text-muted-foreground">%</span>
                        <Button
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          disabled={simulating[app.id]}
                          onClick={() => handleSimulateAssessment(app.id)}
                        >
                          {simulating[app.id] ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Simulate'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </CardContent>
        </Card>
        )}
      </div>

      {/* ── Branding Tab ──────────────────────────────────────────────────────── */}
      {activeSettingsTab === 'branding' && (
        <div className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2"><Palette className="w-5 h-5" /> Colour Palettes</CardTitle>
                <CardDescription>Click a palette to preview it live. Save to make it permanent.</CardDescription>
              </div>
              <Button onClick={saveBranding} disabled={brandingSaving}>
                <Save className="w-4 h-4 mr-2" />{brandingSaving ? 'Saving…' : 'Save Branding'}
              </Button>
            </CardHeader>
            <CardContent className="space-y-8">

              {/* Preset palettes */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-3">Preset Palettes</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {brandingConfig.palettes.filter(p => p.preset).map(palette => (
                    <PaletteCard
                      key={palette.id}
                      palette={palette}
                      active={previewPaletteId === palette.id}
                      saved={brandingConfig.active_palette_id === palette.id}
                      onPreview={() => previewPalette(palette.id)}
                    />
                  ))}
                </div>
              </div>

              {/* Custom palettes */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-gray-700">Custom Palettes</p>
                  <Button size="sm" variant="outline" onClick={openNewCustomPalette}>
                    <Plus className="w-3.5 h-3.5 mr-1.5" />New Custom
                  </Button>
                </div>
                {brandingConfig.palettes.filter(p => !p.preset).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No custom palettes yet. Create one above.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {brandingConfig.palettes.filter(p => !p.preset).map(palette => (
                      <PaletteCard
                        key={palette.id}
                        palette={palette}
                        active={previewPaletteId === palette.id}
                        saved={brandingConfig.active_palette_id === palette.id}
                        onPreview={() => previewPalette(palette.id)}
                        onEdit={() => openEditCustomPalette(palette)}
                        onDelete={() => deleteCustomPalette(palette.id)}
                      />
                    ))}
                  </div>
                )}
              </div>

            </CardContent>
          </Card>

          {/* Custom palette editor dialog */}
          <Dialog open={!!customPaletteForm} onOpenChange={(o) => { if (!o) setCustomPaletteForm(null); }}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingCustomId ? 'Edit' : 'New'} Custom Palette</DialogTitle>
                <DialogDescription>Pick colours for each role. Changes preview live when you click a swatch.</DialogDescription>
              </DialogHeader>
              {customPaletteForm && (
                <div className="space-y-4 py-2">
                  <div className="space-y-1">
                    <Label>Palette Name *</Label>
                    <Input value={customPaletteForm.name} onChange={e => setCustomPaletteForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. My Custom Theme" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      ['primary',    'Primary (sidebar, headings)'],
                      ['primary_fg', 'Primary text'],
                      ['secondary',  'Secondary / CTA (buttons)'],
                      ['secondary_fg','Secondary text'],
                      ['accent',     'Accent (hover surfaces)'],
                      ['accent_fg',  'Accent text'],
                      ['muted',      'Muted (page background)'],
                      ['muted_fg',   'Muted text'],
                      ['background', 'Page background'],
                    ].map(([key, label]) => (
                      <div key={key} className="flex items-center gap-2">
                        <input
                          type="color"
                          value={customPaletteForm.colors[key] || '#000000'}
                          onChange={e => {
                            const updated = { ...customPaletteForm, colors: { ...customPaletteForm.colors, [key]: e.target.value } };
                            setCustomPaletteForm(updated);
                            applyBrandingPalette(updated.colors);
                          }}
                          className="w-8 h-8 rounded cursor-pointer border border-gray-300"
                        />
                        <Label className="text-xs leading-tight">{label}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setCustomPaletteForm(null)}>Cancel</Button>
                <Button onClick={saveCustomPalette}>Save Palette</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Email Preview Dialog */}
      <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Preview: {previewTemplate?.name}</DialogTitle>
            <DialogDescription>Rendered with sample placeholder data</DialogDescription>
          </DialogHeader>
          {previewTemplate && (() => {
            const sample = {
              '{{applicant_name}}': 'Sample Applicant',
              '{{score}}': '85.0',
              '{{test_link}}': 'https://example.com/aptitude-test/sample',
              '{{cohort_name}}': 'Sample Cohort',
              '{{start_date}}': '1 June 2026',
              '{{whatsapp_link}}': 'https://chat.whatsapp.com/sample',
              '{{temp_password}}': 'TempPass123',
              '{{login_url}}': 'https://example.com/login',
            };
            let rendered = previewTemplate.body_html || '';
            Object.entries(sample).forEach(([k, v]) => { rendered = rendered.replaceAll(k, v); });
            return (
              <div className="flex-1 overflow-y-auto border rounded bg-white">
                <iframe
                  srcDoc={rendered}
                  title="Email Preview"
                  className="w-full"
                  style={{ minHeight: '500px', border: 'none' }}
                />
              </div>
            );
          })()}
          <DialogFooter className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setPreviewDialog(false)}>Close</Button>
            <Button
              onClick={async () => {
                setSendingPreview(true);
                try {
                  const token = localStorage.getItem('token');
                  const res = await axios.post(`${API}/email-templates/${previewTemplate.template_key}/preview`, {}, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success(`Test email sent to ${res.data.to}`);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to send test email');
                } finally {
                  setSendingPreview(false);
                }
              }}
              disabled={sendingPreview}
            >
              {sendingPreview ? 'Sending…' : 'Send Test Email to Myself'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Email Template</DialogTitle>
            <DialogDescription>{editingTemplate?.name}</DialogDescription>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-4">
              <div>
                <Label>Subject Line</Label>
                <Input 
                  value={editingTemplate.subject || ''}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, subject: e.target.value }))}
                />
              </div>
              <div>
                <Label>Available Placeholders</Label>
                <div className="flex flex-wrap gap-2 mt-1 mb-2">
                  {(editingTemplate.placeholders || []).map((p, idx) => (
                    <span 
                      key={idx} 
                      className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded cursor-pointer hover:bg-blue-200"
                      onClick={() => {
                        const textarea = document.querySelector('textarea[name="email-body"]');
                        if (textarea) {
                          const pos = textarea.selectionStart || editingTemplate.body_html.length;
                          const newBody = editingTemplate.body_html.slice(0, pos) + `{{${p}}}` + editingTemplate.body_html.slice(pos);
                          setEditingTemplate(prev => ({ ...prev, body_html: newBody }));
                        }
                      }}
                    >
                      {`{{${p}}}`}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mb-2">Click a placeholder to insert it (at the cursor position)</p>
              </div>
              <div>
                <Label>Email Body (HTML)</Label>
                <Textarea 
                  name="email-body"
                  value={editingTemplate.body_html || ''}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, body_html: e.target.value }))}
                  rows={15}
                  className="font-mono text-sm"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveTemplate} disabled={saving}>
              {saving ? 'Saving...' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SiteSettings;
